import {
    IEditorElementError,
    IPage,
    IProjectResult,
    SF,
    ICreateProjectResult,
    IUploadImageResult
} from './interfaces';
import {IFontData} from "@/editor/base/editor.interfaces";
import {AppProxyTokenProvider, postWithCapabilityToken} from './app-proxy-token-provider';
import type {AppProxyTokenTransport, ProtectedIntent} from './app-proxy-token-provider';


interface IFont
{
    css : string;
    faces: {[key:string]:string};
    image: string;
    name: string;
    styles: "b"|"bi"|"i"|"r"[];
}

interface IServerFont extends IFont
{
    public: number;
    directory: string;
}

export interface IThemeData {
    root: {[key:string]:string};
    svg: {
        selector: string;
        replace: string;
        value: string;
    }[]
}

export class CustomizerJsonApi {
    private readonly tokenProvider: AppProxyTokenProvider;

    public constructor(private shopUrl: string,
                       private sessionId: string,
                       private messageEnvironment: string,
                       appProxyAuthUrl?: string,
                       tokenTransport: AppProxyTokenTransport = 'direct',
                       appProxyParentOrigin?: string) {
        this.tokenProvider = new AppProxyTokenProvider({
            transport: tokenTransport,
            authUrl: appProxyAuthUrl,
            parentOrigin: appProxyParentOrigin,
            sessionIdentifier: sessionId
        });
    }

    public saveProjectToCart(projectId: number,
                             productId: number,
                             pages: IPage[],
                             errors: IEditorElementError[],
                             quantity: number = 1,
                             cartPreview: boolean = false,
                             previewKey: string = ''): Promise<IProjectResult> {
        return new Promise<IProjectResult>((resolve: (res: IProjectResult) => void,
                                            reject: (reason: string) => void): void => {
            const params = {
                photo_project_id: projectId.toString(),
                product_id: productId.toString(),
                session_identifier: this.sessionId,
                shop_url: this.shopUrl,
                page_list: JSON.stringify(pages),
                errors: JSON.stringify(errors),
                message_environment: this.messageEnvironment,
                client: 'HTML5_CUSTOMIZER',
                cart_quantity: quantity > 0 ? quantity.toString() : null,
                skip_cart_preview: cartPreview ? 'false' : 'true',
                preview_key: previewKey
            };

            const formData = this.getFormDataFromParams(params);

            this.postPersonalizerForm('AddProjectToCart', formData).then(res => {
                CustomizerJsonApi.handlePost(res, resolve, reject);
            }, err => {
                console.error('Error logging in', err);
                reject(err);
            });
        });
    }

    public uploadImage(file: File, photoProjectId: number):
        Promise<IUploadImageResult> {
        const formData = new FormData();
        formData.append('image', file, file.name);
        formData.append('session_identifier', this.sessionId);
        formData.append('shop_url', this.shopUrl);
        formData.append('message_environment', this.messageEnvironment);
        formData.append('photo_project_id', photoProjectId.toString());

        return new Promise((resolve, reject) => {
            this.postPersonalizerForm('ImageAdd', formData).then(res => {
                CustomizerJsonApi.handlePost<IUploadImageResult>(res, resolve, reject);
            }).catch(err => {
                reject(err.toString());
            });
        });
    }

    public async createProject(partnerSku:string|null,
                        projectGuid:string|null,
                        variantSku:string|null,
                        cartItemId:string|null,
                        photoProjectId:string|null,
                        quantity: string
                        ): Promise<ICreateProjectResult> {
        return new Promise<ICreateProjectResult>((resolve: (res: ICreateProjectResult) => void, reject: (reason: string) => void): void => {
            const params:{[key:string]:string} = {
                cart_item_id: cartItemId,
                photo_project_id: photoProjectId,
                quantity: quantity,
                shop_url: this.shopUrl,
                session_identifier: this.sessionId,
                message_environment: this.messageEnvironment
            };

            if (partnerSku) {
                params.partner_sku = partnerSku;
            } else if (projectGuid) {
                params.project_guid = projectGuid;
                params.variant_sku = variantSku;
            } else {
                throw new Error('No partnerSku or projectGuid specified');
            }

            const formData = this.getFormDataFromParams(params);

            this.postPersonalizerForm('CreateProject', formData).then(res => {
                CustomizerJsonApi.handlePost<ICreateProjectResult>(res, resolve, reject);

            }, err => {
                console.error('Error logging in', err);
                reject(err);
            });
        });
    }

    public getImageFromPostIte(iteUrl:string, iteStr: string) {
        return new Promise<string>((resolve, reject) => {
            const formData = new FormData();

            formData.append('ite', iteStr);
            formData.append('type', 'webp');

            // Make a POST request to the server using FormData
            fetch(iteUrl, {
                method: 'POST',
                body: formData, // Send data as FormData
            })
                .then(response => response.blob()) // Convert the response to a binary Blob
                .then(binaryData => {
                    resolve(URL.createObjectURL(binaryData));
                })
                .catch(error => {
                    reject('Error: ' + JSON.stringify(error));
                });
        });
    }

    public getFontData():Promise<IFontData[]> {
        return new Promise<any[]>((resolve: (res: IFont[]) => void, reject: (error:string) => void): void => {
            const params = {
                shop_url: this.shopUrl,
                session_identifier: this.sessionId,
                message_environment: this.messageEnvironment
            };

            const formData = this.getFormDataFromParams(params);
            this.postPersonalizerForm('GetFonts', formData).then(res => {
                if (res.status === 200) {
                    res.json().then((result:IServerFont[]|{error_message:string}) => {
                        if (Array.isArray(result)) {
                            const fonts = result.map(font => {
                                return {
                                    css: font.directory + '/' + font.css,
                                    faces: Object.fromEntries(
                                        Object.entries(font.faces).map(([key, value]) => [key, font.directory + '/' + value])
                                    ),
                                    image: font.directory + '/' + font.image,
                                    name: font.name,
                                    styles: font.styles,
                                }
                            });

                            resolve(fonts);
                        } else if (result.error_message) {
                            reject(result.error_message);
                        }
                    }, err => {
                        console.error('Error parsing json ', err);
                        reject(err);
                    });
                } else {
                    reject('Server Error: ' + res.statusText);
                }
            }, err => {
                reject(err);
            });
        });
    }

    public getThemeData():Promise<IThemeData> {
        return new Promise<IThemeData>((resolve: (res: IThemeData) => void, reject: (error:string) => void): void => {
            const params = {
                shop_url: this.shopUrl,
                session_identifier: this.sessionId,
                message_environment: this.messageEnvironment
            };

            const formData = this.getFormDataFromParams(params);
            this.postPersonalizerForm('GetTheme', formData).then(res => {
                CustomizerJsonApi.handlePost<IThemeData>(res, resolve, reject);
            }, err => {
                reject(err);
            });
        });
    }

    private getFormDataFromParams(params: { [key: string]: string }): FormData {
        const formData = new FormData();
        Object.keys(params).forEach(key => {
            if (params[key]) {
                formData.append(key, params[key]);
            }
        });
        return formData;
    }

    private async postPersonalizerForm(intent: ProtectedIntent, formData: FormData): Promise<Response> {
        const post = async (token: string): Promise<Response> => fetch(
            SF.personalizerPassThrough.replace(/\/$/, '') + '/Personalizer/' + intent,
            {
                method: 'POST',
                body: formData,
                credentials: 'omit',
                headers: {
                    Authorization: 'Bearer ' + token
                }
            }
        );

        return postWithCapabilityToken(this.tokenProvider, intent, post);
    }


    private static handlePost<T>(res: Response, resolve: (res: T) => void, reject: (reason: string) => void) {
        if (res.status === 200) {
            res.json().then(result => {
                if (result?.error_message || result?.error) {
                    reject(result.error_message || result.error);
                } else if (result) {
                    resolve(result);
                }
            }, err => {
                console.error('Error parsing json ', err);
                reject(err);
            });
        } else {
            res.json().then(result => {
                console.log('Error Result', result);
                // If aspnet.core error result...
                if (result.title && result.detail) {
                    reject(result.title + ': ' + result.detail);
                } else {
                    reject('Server Error: ' + result.error_message);
                }
            }, err=> {
                console.warn(err);
                res.text().then(result => {
                    reject('Server Error: ' + result);
                }, err=> {
                    console.warn(err);
                    reject('Server Error: ' + res.statusText);
                });
            })

        }
    }
}
